import { Link } from 'react-router';
import { z } from "zod";
import Logo from "#app/components/logo.tsx";
import MailingListForm, { type SignupActionData } from "#app/components/mailingListForm.tsx";
import { isSpam } from "#app/utils/honeypot.server.ts";
import { mailingListSignup, SIGNUP_SOURCES } from "#app/utils/mailing-list.server.ts";
import { createRateLimiter, getClientIp } from "#app/utils/rate-limit.server.ts";
import { type Route } from "./+types/subscribe.ts";

export function meta() {
  return [
    { title: "Join the Rohna Mailing List" },
    { name: "description", content: "Sign up for Rohna announcements, early merch drops, and exclusive content." },
    { name: "robots", content: "noindex" },
  ];
}

const EmailSchema = z.string().trim().toLowerCase().email().max(254)
const SourceSchema = z.enum(SIGNUP_SOURCES).catch('website')

const isRateLimited = createRateLimiter({ limit: 20, windowMs: 60_000 })

// Handles signups from every MailingListForm on the site (it posts here).
export async function action({ request }: Route.ActionArgs): Promise<SignupActionData> {
  const formData = await request.formData()

  if (await isSpam(formData)) return { ok: true }

  if (isRateLimited(getClientIp(request))) {
    return { ok: false, message: 'Too many signups from your network. Please wait a minute and try again.' }
  }

  const email = EmailSchema.safeParse(formData.get('email'))
  if (!email.success) return { ok: false, message: 'Please enter a valid email address.' }

  const source = SourceSchema.parse(formData.get('source'))
  const result = await mailingListSignup(email.data, source)
  return { ...result, email: email.data }
}

export default function Subscribe({ actionData }: Route.ComponentProps) {
  return (
    <div className="min-h-svh bg-black flex flex-col items-center justify-center gap-6 py-16 px-4">
      <Link to="/">
        <Logo sizes="w-48 md:w-64" />
      </Link>
      <div className="text-center">
        <h1 className="text-3xl font-extrabold uppercase">Join our mailing list</h1>
        <p className="mt-2 text-sm text-white/70 max-w-xs mx-auto">
          Sign up for announcements, early merch drops and discounts, and other exclusive content. Unsubscribe anytime.
        </p>
      </div>
      <MailingListForm source="show" actionData={actionData ?? undefined} />
    </div>
  )
}
